// src/routes/domains/client/client.routes.tsx

import { lazy } from 'react';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Lazy‑loaded Client components (absolute imports)
// ------------------------------

// Core client portal & dashboard
const AgencyClientDashboard = lazy(() => import('@/pages/client/AgencyClientDashboard'));
const ClientPortal = lazy(() => import('@/pages/client/ClientPortal'));
const ClientDashboard = lazy(() => import('@/pages/ClientDashboard'));

// Case & document management
const ClientCaseDetail = lazy(() => import('@/pages/client/ClientCaseDetail'));
const ClientDocumentHub = lazy(() => import('@/pages/client/ClientDocumentHub'));
const ClientProfile = lazy(() => import('@/pages/client/ClientProfile'));
const CollaborationHub = lazy(() => import('@/pages/client/CollaborationHub'));
const ClientProgressTracking = lazy(() => import('@/pages/client/ClientProgressTracking'));

// Messaging (client‑specific)
const ClientMessages = lazy(() => import('@/pages/messaging/ClientMessages'));

// Additional client‑related components (from agency / global sections)
const ClientsList = lazy(() => import('@/pages/agency/ClientsList.fixed'));
const ClientCreation = lazy(() => import('@/pages/agency/ClientCreation.fixed'));
const ClientJourneyOrchestrator = lazy(() => import('@/pages/agency/ClientJourneyOrchestrator'));
const ClientIntelligence = lazy(() => import('@/pages/agency/ClientIntelligence'));
const GlobalCaseSearch = lazy(() => import('@/pages/agency/GlobalCaseSearch'));
const ClientConsents = lazy(() => import('@/pages/client/ClientConsents'));

// ------------------------------
// Client Routes Definition (with full metadata)
// ------------------------------
export const clientRoutes: AppRoute[] = [
  {
    id: 'client.consents',
    domain: 'client',
    path: '/client/consents',
    element: createProtectedRoute(<ClientConsents />, {
      tier: 'premium',
      meta: { title: 'My Consents - GDPR', region: 'Global', intent: 'consent', aiTags: ['consent','gdpr','client'] },
    }),
  },
  {
    id: 'client.consents.detail',
    domain: 'client',
    path: '/client/consents/:clientId',
    element: createProtectedRoute(<ClientConsents />, {
      tier: 'premium',
      meta: { title: 'My Consents Detail - GDPR', region: 'Global', intent: 'consent', aiTags: ['consent','gdpr','client'] },
    }),
  },
  // Core client dashboards
  {
    id: 'client.dashboard',
    domain: 'client',
    path: '/client/dashboard',
    element: createProtectedRoute(<AgencyClientDashboard />, {
      tier: 'premium',
      meta: {
        title: 'Client Dashboard',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['client', 'dashboard', 'overview'],
      },
    }),
  },
  {
    id: 'client.portal',
    domain: 'client',
    path: '/client/portal',
    element: createProtectedRoute(<ClientPortal />, {
      tier: 'premium',
      meta: {
        title: 'Client Portal',
        region: 'Global',
        intent: 'portal',
        aiTags: ['client', 'portal', 'self-service'],
      },
    }),
  },
  {
    id: 'client.dashboard_alt',
    domain: 'client',
    path: '/client-dashboard',
    element: createProtectedRoute(<ClientDashboard />, {
      tier: 'premium',
      meta: {
        title: 'Client Dashboard (Alt)',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['client', 'dashboard', 'alternative'],
      },
    }),
  },

  // Case & document management
  {
    id: 'client.case_detail',
    domain: 'client',
    path: '/client/case/:id',
    element: createProtectedRoute(<ClientCaseDetail />, {
      tier: 'premium',
      meta: {
        title: 'Client Case Details',
        region: 'Global',
        intent: 'case',
        aiTags: ['case', 'detail', 'client'],
      },
    }),
  },
  {
    id: 'client.documents',
    domain: 'client',
    path: '/client/documents',
    element: createProtectedRoute(<ClientDocumentHub />, {
      tier: 'premium',
      meta: {
        title: 'Client Document Hub',
        region: 'Global',
        intent: 'document',
        aiTags: ['documents', 'hub', 'client'],
      },
    }),
  },
  {
    id: 'client.profile',
    domain: 'client',
    path: '/client/profile',
    element: createProtectedRoute(<ClientProfile />, {
      tier: 'premium',
      meta: {
        title: 'Client Profile',
        region: 'Global',
        intent: 'profile',
        aiTags: ['profile', 'settings', 'client'],
      },
    }),
  },
  {
    id: 'client.collaboration',
    domain: 'client',
    path: '/client/collaboration',
    element: createProtectedRoute(<CollaborationHub />, {
      tier: 'premium',
      meta: {
        title: 'Client Collaboration Hub',
        region: 'Global',
        intent: 'collaboration',
        aiTags: ['collaboration', 'team', 'client'],
      },
    }),
  },
  {
    id: 'client.progress',
    domain: 'client',
    path: '/client/progress',
    element: createProtectedRoute(<ClientProgressTracking />, {
      tier: 'premium',
      meta: {
        title: 'Client Progress Tracking',
        region: 'Global',
        intent: 'tracking',
        aiTags: ['progress', 'tracking', 'milestones'],
      },
    }),
  },

  // Messaging
  {
    id: 'client.messages',
    domain: 'client',
    path: '/client/messages',
    element: createProtectedRoute(<ClientMessages />, {
      tier: 'premium',
      meta: {
        title: 'Client Messages',
        region: 'Global',
        intent: 'chat',
        aiTags: ['messages', 'inbox', 'client'],
      },
    }),
  },

  // Additional agency‑style client management tools (optional)
  {
    id: 'client.agency_clients_list',
    domain: 'client',
    path: '/client/agency/clients',
    element: createProtectedRoute(<ClientsList />, {
      tier: 'premium',
      meta: {
        title: 'Clients List (Agency View)',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['clients', 'list', 'agency'],
      },
    }),
  },
  {
    id: 'client.agency_client_creation',
    domain: 'client',
    path: '/client/agency/clients/new',
    element: createProtectedRoute(<ClientCreation />, {
      tier: 'premium',
      meta: {
        title: 'Create New Client (Agency)',
        region: 'Global',
        intent: 'form',
        aiTags: ['client', 'create', 'agency'],
      },
    }),
  },
  {
    id: 'client.journey_orchestrator',
    domain: 'client',
    path: '/client/journey-orchestrator',
    element: createProtectedRoute(<ClientJourneyOrchestrator />, {
      tier: 'premium',
      meta: {
        title: 'Client Journey Orchestrator',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['journey', 'orchestrator', 'client'],
      },
    }),
  },
  {
    id: 'client.intelligence',
    domain: 'client',
    path: '/client/intelligence',
    element: createProtectedRoute(<ClientIntelligence />, {
      tier: 'premium',
      meta: {
        title: 'Client Intelligence Dashboard',
        region: 'Global',
        intent: 'dashboard',
        aiTags: ['client', 'intelligence', 'analytics'],
      },
    }),
  },
  {
    id: 'client.global_case_search',
    domain: 'client',
    path: '/client/global-case-search',
    element: createProtectedRoute(<GlobalCaseSearch />, {
      tier: 'premium',
      meta: {
        title: 'Global Case Search',
        region: 'Global',
        intent: 'search',
        aiTags: ['case', 'search', 'global'],
      },
    }),
  },
];